import { Button } from '@thanx/uikit/button'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('recovery_ai.gaming_review.pagination')

type Props = {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

// Copied from RestrictedCustomers/Pagination (honoring the no-cross-sub-scene
// import rule) with the gaming_review locale namespace. The optional shared
// PaginationControls de-dup is deferred to its own unit (see the plan).
const Pagination = ({ currentPage, totalPages, onPageChange }: Props) => {
  const [css] = useStyletron()
  const isFirst = currentPage <= 1
  const isLast = currentPage >= totalPages

  // Local draft mirrors what the user is typing so the number input can be
  // transiently empty/invalid without the controlled `value` snapping it back
  // mid-keystroke; re-sync on canonical page change; blur snaps back.
  const [draft, setDraft] = useState(String(currentPage))
  useEffect(() => {
    setDraft(String(currentPage))
  }, [currentPage])

  const commitDraft = (raw: string) => {
    setDraft(raw)
    const page = parseInt(raw, 10)
    if (isNaN(page)) return
    onPageChange(Math.min(Math.max(page, 1), totalPages))
  }

  return (
    <div
      className={`d-flex align-items-center justify-content-center ${css({
        padding: '16px 0',
        gap: '8px',
      })}`}
    >
      <Button
        kind="minimal"
        disabled={isFirst}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <i className="fa fa-chevron-left" aria-hidden="true" /> {t('prev')}
      </Button>

      <span
        className={css({
          fontSize: '14px',
          color: merchantTheme.colors.grey70,
        })}
      >
        {t('page')}
      </span>
      <input
        type="number"
        min={1}
        max={totalPages}
        value={draft}
        onChange={e => commitDraft(e.target.value)}
        onBlur={() => setDraft(String(currentPage))}
        className={css({
          width: '48px',
          textAlign: 'center',
          border: `1px solid ${merchantTheme.colors.grey30}`,
          borderRadius: '4px',
          padding: '4px',
          fontSize: '14px',
        })}
        aria-label={t('page')}
      />
      <span
        className={css({
          fontSize: '14px',
          color: merchantTheme.colors.grey50,
        })}
      >
        {t('of', { total: totalPages })}
      </span>

      <Button
        kind="minimal"
        disabled={isLast}
        onClick={() => onPageChange(currentPage + 1)}
      >
        {t('next')} <i className="fa fa-chevron-right" aria-hidden="true" />
      </Button>
    </div>
  )
}

export default Pagination
